import { t } from '@trimble/i18n';
import {
  Component,
  Show,
  batch,
  createEffect,
  createSignal,
  createUniqueId,
} from 'solid-js';
import {
  IconButton,
  BtnClasses,
  defineSolidModusTextInput,
} from '@trimble/modus-ui';

const TEXT_INPUT_TAG = 'tc-modus-text-input';

// Registered at module scope so the element is upgraded before the first
// render sets properties on it.
if (typeof window !== 'undefined' && !customElements.get(TEXT_INPUT_TAG)) {
  defineSolidModusTextInput();
}

export interface ModusEditableTitleProps {
  label: string;
  handleEditLabelSave(label: string | undefined): void;
  hideTitleEdit?: boolean;
  disableEdit?: boolean;
  disabledReason?: string;
}

export const ModusEditableTitle: Component<ModusEditableTitleProps> = (
  props
) => {
  const editLabelInputId = createUniqueId();
  const [isEditingTitle, setIsEditingTitle] = createSignal(false);
  const [editingLabel, setEditingLabel] = createSignal<string>();
  let inputRef: HTMLElement | undefined;

  createEffect(() => {
    if (!isEditingTitle()) return;

    // The custom element renders its inner <input> into its shadow root only
    // once it upgrades, so the element has to be defined before it can be
    // focused.
    void customElements.whenDefined(TEXT_INPUT_TAG).then(() => {
      queueMicrotask(() => {
        const input = inputRef?.shadowRoot?.querySelector('input');
        input?.focus();
        input?.select();
      });
    });
  });

  const handleEditButtonClick = (): void => {
    if (props.disableEdit) return;
    batch(() => {
      setEditingLabel(props.label);
      setIsEditingTitle(true);
    });
  };

  const handleLabelChange = (event: CustomEvent<{ value: string }>): void => {
    setEditingLabel(event.detail.value);
  };

  const handleLabelInputBlur = (): void => {
    setIsEditingTitle(false);
    props.handleEditLabelSave(editingLabel());
  };

  return (
    <Show
      when={!isEditingTitle()}
      fallback={
        <div class="flex flex-1 min-w-0 items-center gap-2 px-2">
          <tc-modus-text-input
            ref={(element: HTMLElement) => {
              inputRef = element;
            }}
            class="flex-1 min-w-0"
            // The element's inner wrapper is `display: contents`, so the host
            // itself has to be the layout box for `flex-1` to apply.
            style={{ display: 'block' }}
            prop:shadowDom={true}
            prop:inputId={editLabelInputId}
            prop:name={editLabelInputId}
            prop:size="sm"
            prop:customClass="!mb-0"
            prop:value={editingLabel() ?? ''}
            prop:placeholder={props.label}
            prop:ariaLabel={t('general.edit')}
            on:input-change={handleLabelChange}
            on:input-blur={handleLabelInputBlur}
            onKeyDown={(event: KeyboardEvent) => {
              if (event.key === 'Enter') {
                (event.composedPath()[0] as HTMLInputElement).blur();
              }
            }}
          />
        </div>
      }
    >
      <div class="flex flex-1 min-w-0 items-center justify-between">
        <div
          data-testid="editable-title-label"
          class="text-lg font-semibold px-2 truncate min-w-0"
          textContent={props.label}
        />

        <Show when={!props.hideTitleEdit}>
          <IconButton
            color={BtnClasses.Tertiary}
            icon="pencil"
            size="icon-medium"
            data-cy="edit-icon"
            clicked={handleEditButtonClick}
            title={
              props.disableEdit
                ? (props.disabledReason ?? '')
                : t('general.edit')
            }
            disabled={props.disableEdit}
          />
        </Show>
      </div>
    </Show>
  );
};
